import React, { useState, useEffect } from 'react';
import TextInputField from 'components/TextInputField';
import MagicButton from 'components/MagicButton/MagicButton';
import StatusMessage from 'components/StatusMessage';
import CountrySelect from 'components/CountrySelect';
import { useGetProfileQuery, useUpdateProfileMutation, useGetEmailChangeStatusQuery, useResendVerificationMutation } from 'store/services/auth/authApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useGetNotificationSettingsQuery, useUpdateNotificationSettingsMutation } from 'store/services/message/messageApi';
import { useGetWantListDefaultsQuery, useUpdateWantListDefaultsMutation } from 'store/services/settings/settingsApi';
import EmailChangeModal from 'components/EmailChangeModal/EmailChangeModal';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';
import ToggleSwitch from 'components/ToggleSwitch';
import InlineFoilPills from 'components/InlineFoilPills/InlineFoilPills';
import InlineConditionPills from 'components/InlineConditionPills/InlineConditionPills';
import { AssetUrls } from 'config/assets';
import { toast } from 'react-toastify';
import './Settings.css';

export default function Settings() {
  const [activeTab, setActiveTab] = useState<'Profile' | 'Preferences'>('Profile'); // Removed 'Billing' from type
  const [isEditMode, setIsEditMode] = useState(false);
  const tabs = ['Profile', 'Preferences']; // Temporarily removed 'Billing' tab

  const [form, setForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    email: '',
    phone: '',
  });

  const [originalForm, setOriginalForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    email: '',
    phone: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);

  // API hooks
  const { data: profileData, isLoading: isLoadingProfile, error: profileError } = useGetProfileQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();
  const { data: emailChangeStatus, refetch: refetchEmailStatus } = useGetEmailChangeStatusQuery();
  const { data: userData } = useGetMeQuery();
  const [resendVerification] = useResendVerificationMutation();
  const { data: notificationSettings, isLoading: isLoadingNotifications } = useGetNotificationSettingsQuery();
  const [updateNotificationSettings, { isLoading: isUpdatingNotifications }] = useUpdateNotificationSettingsMutation();
  const { data: wantListDefaults, isLoading: isLoadingWantListDefaults } = useGetWantListDefaultsQuery();
  const [updateWantListDefaults, { isLoading: isUpdatingWantListDefaults }] = useUpdateWantListDefaultsMutation();

  // Load profile data on component mount
  useEffect(() => {
    if (profileData) {
      console.log('📍 Profile data received:', profileData);
      const formData = {
        username: profileData.username || '',
        // Check for nested shippingAddress object (new structure) or flat fields (old structure)
        firstName: profileData.shippingAddress?.firstName || profileData.shippingFirstName || '',
        lastName: profileData.shippingAddress?.lastName || profileData.shippingLastName || '',
        address1: profileData.shippingAddress?.address1 || profileData.shippingAddress1 || '',
        address2: profileData.shippingAddress?.address2 || profileData.shippingAddress2 || '',
        city: profileData.shippingAddress?.city || profileData.shippingCity || '',
        state: profileData.shippingAddress?.state || profileData.shippingState || '',
        zip: profileData.shippingAddress?.postalCode || profileData.shippingPostalCode || '',
        country: profileData.shippingAddress?.country || profileData.shippingCountry || '',
        email: profileData.email || '',
        phone: profileData.phoneNumber || profileData.phone || '',
      };
      console.log('📍 Form data mapped:', formData);
      setForm(formData);
      setOriginalForm(formData);
    }
  }, [profileData]);

  // Refetch email change status when component mounts or when profile data changes
  useEffect(() => {
    refetchEmailStatus();
  }, [profileData, refetchEmailStatus]);


  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    // Clear save status when user makes changes
    setSaveStatus(null);
  };

  // Check if form has changes
  const hasChanges = () => {
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  };

  // Validate required fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!form.username.trim()) errors.username = 'Username is required';
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.address1.trim()) errors.address1 = 'Address line 1 is required';
    if (!form.city.trim()) errors.city = 'City is required';
    if (!form.state.trim()) errors.state = 'State/Province is required';
    if (!form.zip.trim()) errors.zip = 'ZIP/Postal code is required';
    if (!form.country) errors.country = 'Country is required';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setSaveStatus(null);
  };

  const handleCancel = () => {
    // Reset form to original values
    setForm(originalForm);
    setIsEditMode(false);
    setValidationErrors({});
    setSaveStatus(null);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setSaveStatus({ type: 'error', message: 'Please fix the errors above before saving.' });
      return;
    }

    try {
      const profileUpdateData = {
        username: form.username,
        firstName: form.firstName,
        lastName: form.lastName,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state: form.state,
        postalCode: form.zip,
        country: form.country,
        phone: form.phone,
      };

      console.log('💾 Saving profile data:', profileUpdateData);
      const result = await updateProfile(profileUpdateData).unwrap();

      console.log('✅ Profile saved successfully:', result);
      setSaveStatus({ type: 'success', message: 'Profile updated successfully!' });

      // Update original form to reflect saved state
      setOriginalForm({ ...form });

      // Exit edit mode after successful save
      setIsEditMode(false);

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error: any) {
      console.error('❌ Failed to save profile:', error);
      const errorMessage = error?.data?.message || 'Failed to save profile. Please try again.';
      setSaveStatus({ type: 'error', message: errorMessage });
    }
  };

  const handleEmailNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnNewMessage: checked }).unwrap();
      toast.success(
        checked
          ? 'Email notifications enabled'
          : 'Email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleTradeNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnNewTrade: checked }).unwrap();
      toast.success(
        checked
          ? 'Trade email notifications enabled'
          : 'Trade email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleSystemMessageNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnSystemMessage: checked }).unwrap();
      toast.success(
        checked
          ? 'System message email notifications enabled'
          : 'System message email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  const handleNewFeaturesNotificationToggle = async (checked: boolean) => {
    try {
      await updateNotificationSettings({ emailOnNewFeatures: checked }).unwrap();
      toast.success(
        checked
          ? 'New features email notifications enabled'
          : 'New features email notifications disabled'
      );
    } catch (error: any) {
      console.error('Failed to update notification settings:', error);
      toast.error('Failed to update notification preferences. Please try again.');
    }
  };

  // Want List Defaults Handlers
  const handleFoilChange = async (acceptFoil: boolean, acceptNonFoil: boolean) => {
    // Validate at least one is selected
    if (!acceptFoil && !acceptNonFoil) {
      toast.error('At least one foil option must be selected');
      return;
    }

    try {
      await updateWantListDefaults({
        defaultAcceptFoil: acceptFoil,
        defaultAcceptNonFoil: acceptNonFoil,
      }).unwrap();
      toast.success('Preference updated');
    } catch (error: any) {
      console.error('Failed to update want list defaults:', error);
      toast.error('Failed to update preferences. Please try again.');
    }
  };

  const handleConditionChange = async (conditions: string[]) => {
    // Validate at least one is selected
    if (conditions.length === 0) {
      toast.error('At least one condition must be selected');
      return;
    }

    try {
      await updateWantListDefaults({
        defaultConditions: conditions,
      }).unwrap();
      toast.success('Preference updated');
    } catch (error: any) {
      console.error('Failed to update want list defaults:', error);
      toast.error('Failed to update preferences. Please try again.');
    }
  };

  const handleLanguageChange = async (languages: string[]) => {
    // Validate at least one is selected
    if (languages.length === 0) {
      toast.error('At least one language must be selected');
      return;
    }

    try {
      await updateWantListDefaults({
        defaultLanguages: languages,
      }).unwrap();
      toast.success('Preference updated');
    } catch (error: any) {
      console.error('Failed to update want list defaults:', error);
      toast.error('Failed to update preferences. Please try again.');
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        {tabs.map((tab) => (
          <div
            key={tab}
            className={`settings-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab as any)}
          >
            {tab}
          </div>
        ))}
      </div>

      <div className="settings-body">
        {activeTab === 'Profile' && (
          <div className="settings-form">
            <>
            {/* Profile Summary Card */}
            {profileData && (
              <div className="profile-summary-card">
                {hasChanges() && isEditMode && (
                  <div className="profile-summary-header">
                    <span className="unsaved-indicator">• Unsaved changes</span>
                  </div>
                )}
                <div className="profile-summary-content">
                  <div className="summary-item">
                    <span className="summary-label">Username:</span>
                    <span className="summary-value">{profileData.username || 'Not set'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Email:</span>
                    <span className="summary-value">{profileData.email}</span>
                  </div>
                  {(profileData.shippingAddress || profileData.shippingFirstName) && (
                    <div className="summary-item">
                      <span className="summary-label">Shipping Address:</span>
                      <span className="summary-value address-multiline">
                        {profileData.shippingAddress ? (
                          <>
                            {profileData.shippingAddress.firstName && profileData.shippingAddress.lastName && (
                              <div>{profileData.shippingAddress.firstName} {profileData.shippingAddress.lastName}</div>
                            )}
                            {profileData.shippingAddress.address1 && (
                              <div>{profileData.shippingAddress.address1}</div>
                            )}
                            {profileData.shippingAddress.address2 && (
                              <div>{profileData.shippingAddress.address2}</div>
                            )}
                            {profileData.shippingAddress.city && profileData.shippingAddress.state && (
                              <div>{profileData.shippingAddress.city}, {profileData.shippingAddress.state} {profileData.shippingAddress.postalCode}</div>
                            )}
                            {profileData.shippingAddress.country && (
                              <div>{profileData.shippingAddress.country}</div>
                            )}
                          </>
                        ) : (
                          <>
                            {profileData.shippingFirstName && profileData.shippingLastName && (
                              <div>{profileData.shippingFirstName} {profileData.shippingLastName}</div>
                            )}
                            {profileData.shippingAddress1 && (
                              <div>{profileData.shippingAddress1}</div>
                            )}
                            {profileData.shippingAddress2 && (
                              <div>{profileData.shippingAddress2}</div>
                            )}
                            {profileData.shippingCity && profileData.shippingState && (
                              <div>{profileData.shippingCity}, {profileData.shippingState} {profileData.shippingPostalCode}</div>
                            )}
                            {profileData.shippingCountry && (
                              <div>{profileData.shippingCountry}</div>
                            )}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="summary-item">
                    <span className="summary-label">Phone:</span>
                    <span className="summary-value" style={!profileData.phoneNumber ? { color: '#ff4444' } : undefined}>
                      {profileData.phoneNumber || 'Not provided'}
                    </span>
                  </div>
                </div>
                {!isEditMode && (
                  <div className="profile-edit-button-container">
                    <MagicButton
                      label="Edit Profile"
                      color="purple"
                      onPress={handleEdit}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Empty State Message */}
            {!isLoadingProfile && !profileData?.username && !profileData?.shippingAddress?.firstName && !profileData?.shippingFirstName && (
              <div className="empty-state-message">
                <p>👋 Welcome! Please complete your profile information below.</p>
              </div>
            )}
            
            {/* Edit Mode Form Fields */}
            {isEditMode && (
              <>
            {/* Username */}
            <h3 className="settings-section-title">Username</h3>
            <TextInputField
              placeholder={form.username ? "Enter username" : "Choose a unique username"}
              value={form.username}
              onChange={(val) => handleChange('username', val)}
            />
            {validationErrors.username && (
              <div className="validation-error">{validationErrors.username}</div>
            )}

            {/* Shipping Address */}
            <h3 className="settings-section-title">Shipping Address</h3>
            <div className="settings-row">
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.firstName ? "First Name" : "Enter your first name"}
                  value={form.firstName}
                  onChange={(val) => handleChange('firstName', val)}
                />
                {validationErrors.firstName && (
                  <div className="validation-error">{validationErrors.firstName}</div>
                )}
              </div>
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.lastName ? "Last Name" : "Enter your last name"}
                  value={form.lastName}
                  onChange={(val) => handleChange('lastName', val)}
                />
                {validationErrors.lastName && (
                  <div className="validation-error">{validationErrors.lastName}</div>
                )}
              </div>
            </div>

            <TextInputField
              placeholder={form.address1 ? "Address Line 1" : "Street address or P.O. box"}
              value={form.address1}
              onChange={(val) => handleChange('address1', val)}
            />
            {validationErrors.address1 && (
              <div className="validation-error">{validationErrors.address1}</div>
            )}
            <TextInputField
              placeholder="Address Line 2 (Optional)"
              value={form.address2}
              onChange={(val) => handleChange('address2', val)}
            />

            <div className="settings-row">
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.city ? "City" : "Enter your city"}
                  value={form.city}
                  onChange={(val) => handleChange('city', val)}
                />
                {validationErrors.city && (
                  <div className="validation-error">{validationErrors.city}</div>
                )}
              </div>
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.state ? "State / Province" : "e.g., CA, NY, TX"}
                  value={form.state}
                  onChange={(val) => handleChange('state', val)}
                />
                {validationErrors.state && (
                  <div className="validation-error">{validationErrors.state}</div>
                )}
              </div>
            </div>

            <div className="settings-row">
              <div className="settings-field-container">
                <TextInputField
                  placeholder={form.zip ? "ZIP / Postal Code" : "e.g., 10001"}
                  value={form.zip}
                  onChange={(val) => handleChange('zip', val)}
                />
                {validationErrors.zip && (
                  <div className="validation-error">{validationErrors.zip}</div>
                )}
              </div>
              <div className="settings-field-container">
                <CountrySelect
                  value={form.country}
                  onChange={(val) => handleChange('country', val)}
                  error={validationErrors.country}
                />
              </div>
            </div>

            {/* Contact Info */}
            <h3 className="settings-section-title">Contact Info</h3>
            <div className="email-field-wrapper">
              <div className="read-only-field" style={{ flex: 1 }}>
                <TextInputField
                  placeholder="Email"
                  value={form.email}
                  onChange={() => {}} // Read-only
                />
                <div className="read-only-overlay" />
              </div>
              <button
                className="email-change-button"
                onClick={() => setIsEmailModalOpen(true)}
                type="button"
              >
                Change
              </button>
            </div>
            {/* Email verification status */}
            {userData && !userData.emailVerified && (
              <div className="email-verification-notice">
                <span className="warning-icon">⚠️</span>
                <span>Email not verified</span>
                <button
                  className="resend-verification-button"
                  onClick={async () => {
                    try {
                      await resendVerification().unwrap();
                      toast.success('Verification email sent! Please check your inbox.');
                    } catch (error) {
                      toast.error('Failed to send verification email. Please try again.');
                    }
                  }}
                  type="button"
                >
                  Resend Verification
                </button>
              </div>
            )}
            {userData && userData.emailVerified && (
              <div className="email-verified-notice">
                <span className="verified-icon">✅</span>
                <span>Email verified</span>
              </div>
            )}
            {emailChangeStatus?.pendingEmail && (
              <div className="pending-email-notice">
                <span className="pending-icon">⏳</span>
                Pending change to: {emailChangeStatus.pendingEmail}
              </div>
            )}
            <div className="phone-field-wrapper">
              <div className="read-only-field" style={{ flex: 1 }}>
                <TextInputField
                  placeholder={form.phone ? "Phone" : "Phone number (Optional)"}
                  value={form.phone}
                  onChange={() => {}} // Read-only
                />
                <div className="read-only-overlay" />
              </div>
              <button
                className="phone-change-button"
                onClick={() => setIsPhoneModalOpen(true)}
                type="button"
              >
                {userData?.phoneVerified ? 'Change' : 'Verify Now'}
              </button>
            </div>
            {/* Phone verification status */}
            {userData && !userData.phoneVerified && (
              <div className="phone-verification-notice">
                <span className="warning-icon">⚠️</span>
                <span>Phone not verified</span>
              </div>
            )}
            {userData && userData.phoneVerified && (
              <div className="phone-verified-notice">
                <span className="verified-icon">✅</span>
                <span>Phone verified</span>
              </div>
            )}
              </>
            )}

            {/* Save Status Messages */}
            {isLoadingProfile && (
              <div className="loading-message">Loading profile data...</div>
            )}
            {profileError && (
              <StatusMessage 
                type="error" 
                message="Failed to load profile data. Please refresh the page." 
              />
            )}
            {saveStatus && (
              <StatusMessage 
                type={saveStatus.type} 
                message={saveStatus.message} 
              />
            )}

            {/* Action Buttons */}
            {isEditMode && (
              <div className="settings-button-container">
                <MagicButton 
                  label="Cancel" 
                  color="dark-grey" 
                  onPress={handleCancel}
                  isDisabled={isSaving}
                />
                <MagicButton 
                  label={isSaving ? "Saving..." : "Save Changes"} 
                  color="purple" 
                  onPress={handleSave}
                  isDisabled={!hasChanges() || isSaving || isLoadingProfile}
                />
              </div>
            )}
            </>
          </div>
        )}
        {activeTab === 'Preferences' && (
          <div className="settings-form">
            <h3 className="settings-section-title">Email Notifications</h3>
            <div className="notification-setting-item">
              <div className="notification-setting-content">
                <div className="notification-setting-label">Message Notifications</div>
                <div className="notification-setting-description">
                  Receive an email when someone sends you a message
                </div>
              </div>
              <ToggleSwitch
                checked={notificationSettings?.emailOnNewMessage ?? true}
                onChange={handleEmailNotificationToggle}
                disabled={isLoadingNotifications || isUpdatingNotifications}
              />
            </div>
            <div className="notification-setting-item">
              <div className="notification-setting-content">
                <div className="notification-setting-label">Trade Notifications</div>
                <div className="notification-setting-description">
                  Email me when someone sends me cards
                </div>
              </div>
              <ToggleSwitch
                checked={notificationSettings?.emailOnNewTrade ?? true}
                onChange={handleTradeNotificationToggle}
                disabled={isLoadingNotifications || isUpdatingNotifications}
              />
            </div>
            <div className="notification-setting-item">
              <div className="notification-setting-content">
                <div className="notification-setting-label">System Message Notifications</div>
                <div className="notification-setting-description">
                  Receive an email when TradeMagic sends you a system message
                </div>
              </div>
              <ToggleSwitch
                checked={notificationSettings?.emailOnSystemMessage ?? true}
                onChange={handleSystemMessageNotificationToggle}
                disabled={isLoadingNotifications || isUpdatingNotifications}
              />
            </div>
            <div className="notification-setting-item">
              <div className="notification-setting-content">
                <div className="notification-setting-label">New Features</div>
                <div className="notification-setting-description">
                  Receive an email when new features are published on TradeMagic
                </div>
              </div>
              <ToggleSwitch
                checked={notificationSettings?.emailOnNewFeatures ?? true}
                onChange={handleNewFeaturesNotificationToggle}
                disabled={isLoadingNotifications || isUpdatingNotifications}
              />
            </div>

            {/* Want List Defaults Section */}
            <h3 className="settings-section-title" style={{ marginTop: '40px' }}>Version Defaults - Want List</h3>
            <p className="settings-section-description">
              Set default card versions for your want list. These versions will be automatically added when available.
            </p>

            <div className="version-defaults-container">
              {/* Foil Section */}
              <div className="version-default-item">
                <label className="version-default-label">Foil</label>
                <InlineFoilPills
                  acceptFoil={wantListDefaults?.defaultAcceptFoil ?? true}
                  acceptNonFoil={wantListDefaults?.defaultAcceptNonFoil ?? true}
                  onChange={handleFoilChange}
                  disabled={isLoadingWantListDefaults || isUpdatingWantListDefaults}
                />
              </div>

              {/* Condition Section */}
              <div className="version-default-item">
                <label className="version-default-label">Condition</label>
                <InlineConditionPills
                  value={wantListDefaults?.defaultConditions ?? ['NM', 'LP', 'MP']}
                  onChange={handleConditionChange}
                  disabled={isLoadingWantListDefaults || isUpdatingWantListDefaults}
                />
              </div>

              {/* Language Section */}
              <div className="version-default-item">
                <label className="version-default-label">Language</label>
                <div className="language-pills-container">
                  {[
                    { code: 'EN', name: 'English' },
                    { code: 'ES', name: 'Spanish' },
                    { code: 'FR', name: 'French' },
                    { code: 'DE', name: 'German' },
                    { code: 'IT', name: 'Italian' },
                    { code: 'PT', name: 'Portuguese' },
                    { code: 'JA', name: 'Japanese' },
                    { code: 'KO', name: 'Korean' },
                    { code: 'RU', name: 'Russian' },
                    { code: 'ZHS', name: 'Chinese Simplified' },
                    { code: 'ZHT', name: 'Chinese Traditional' },
                  ].map((lang) => {
                    const isActive = (wantListDefaults?.defaultLanguages ?? ['EN']).includes(lang.code);
                    return (
                      <button
                        key={lang.code}
                        className={`filter-pill ${isActive ? 'active' : ''}`}
                        onClick={() => {
                          const currentLanguages = wantListDefaults?.defaultLanguages ?? ['EN'];
                          const newLanguages = isActive
                            ? currentLanguages.filter((l) => l !== lang.code)
                            : [...currentLanguages, lang.code];
                          handleLanguageChange(newLanguages);
                        }}
                        disabled={isLoadingWantListDefaults || isUpdatingWantListDefaults}
                      >
                        <span>{lang.name}</span>
                        {isActive && (
                          <img
                            src={AssetUrls.icons.dropdowns.close}
                            alt="close"
                            className="filter-pill-close"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* {activeTab === 'Billing' && <p>Billing content goes here...</p>} */}
      </div>

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          refetchEmailStatus();
        }}
        currentEmail={form.email}
      />

      {/* Mobile Verification Modal */}
      <MobileVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={() => {
          setIsPhoneModalOpen(false);
        }}
        currentPhone={userData?.phoneNumber}
        onVerificationSuccess={() => {
          // Refresh user data to get updated phone verification status
          window.location.reload();
        }}
      />
    </div>
  );
}